import { useMemo, useState, useCallback, useEffect } from 'react';
import { SelectFilterDef } from '@tanstack/react-table';
import { UseInfiniteScrollHookResult } from 'react-infinite-scroll-hook';
import { EMPTY_FILTER_VALUE } from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import {
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  cn,
  filterOptionsByLabel,
} from '@sib/ui';
import { Search, X } from '@sib/ui/icons';
import { debounce } from 'lodash';

export interface Option {
  value: string;
  label: string;
}

interface SelectFilterProps {
  options: Option[];
  value: string[];
  onChange?: (newValues: string[]) => void;
  placeholder?: string;
  isSingleSelect?: boolean;
  disabled?: boolean;
  debounceMs?: number;
  onSearchTermChange?: (searchTerm: string) => void;
  /** Shows a search input that filters `options` on the client. Ignored when `onSearchTermChange` is set. */
  searchable?: boolean;
  sentryRef?: UseInfiniteScrollHookResult['0'];
  hasNextPage?: boolean;
  isLoading?: boolean;
  PopoverProps?: SelectFilterDef['PopoverProps'];
}

export const SelectFilter = ({
  options,
  value,
  onChange,
  placeholder,
  isSingleSelect = false,
  disabled = false,
  debounceMs = 500,
  onSearchTermChange,
  searchable = false,
  sentryRef,
  isLoading,
  hasNextPage,
  PopoverProps,
}: SelectFilterProps) => {
  const { TriggerProps, ContentProps } = PopoverProps ?? {};
  const { className: triggerClassName, ...TriggerButtonProps } =
    TriggerProps ?? {};
  const { className: contentClassName, ...PopoverContentProps } =
    ContentProps ?? {};
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [internalValue, setInternalValue] = useState<string[]>(value);
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  const isLocalSearch = searchable && !onSearchTermChange;
  const visibleOptions = useMemo(
    () =>
      isLocalSearch ? filterOptionsByLabel(options, localSearchTerm) : options,
    [isLocalSearch, options, localSearchTerm]
  );

  const selectedCount = internalValue.filter(
    value => value !== EMPTY_FILTER_VALUE
  ).length;

  const areAllSelected = selectedCount === options.length && options.length > 0;

  const isIndeterminate = selectedCount > 0 && !areAllSelected;

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (!open) onSearchTermChange?.('');
  }, [open, onSearchTermChange]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setLocalSearchTerm('');
  }, []);

  const debounceOnChange = useMemo(
    () =>
      debounce((newValues: string[]) => {
        onChange?.(newValues);
      }, debounceMs),
    [onChange, debounceMs]
  );

  const handleOnChange = useCallback(
    (newValues: string[]) => {
      setInternalValue(newValues);
      debounceOnChange(newValues);
    },
    [debounceOnChange]
  );

  const toggleAll = () => {
    handleOnChange(areAllSelected ? [] : options.map(opt => opt.value));
  };

  const toggle = (val: string) => {
    const baseValues =
      val !== EMPTY_FILTER_VALUE
        ? internalValue.filter(v => v !== EMPTY_FILTER_VALUE)
        : internalValue;

    if (isSingleSelect) handleOnChange(baseValues.includes(val) ? [] : [val]);
    else
      handleOnChange(
        baseValues.includes(val)
          ? baseValues.filter(item => item !== val)
          : [...baseValues, val]
      );
  };

  const handleClear = useCallback(() => {
    handleOnChange([]);
  }, [handleOnChange]);

  const buttonLabel = useMemo(() => {
    const defaultPlaceholder = placeholder || '';
    const selectedLabel = t('components.selectFilter.selected', {
      count: selectedCount,
    });

    if (internalValue.length === 0) return defaultPlaceholder;
    if (internalValue.length === 1 && internalValue[0] === EMPTY_FILTER_VALUE)
      return (
        options.find(option => option.value === EMPTY_FILTER_VALUE)?.label ??
        t('components.dataTable.emptyFilter.optionLabel')
      );

    if (internalValue.length === 1)
      return (
        options.find(opt => opt.value === internalValue[0])?.label ??
        selectedLabel
      );

    return selectedLabel;
  }, [placeholder, t, internalValue, options, selectedCount]);

  const debounceOnSearchTermChange = useMemo(
    () =>
      debounce((value: string) => {
        onSearchTermChange?.(value);
      }, debounceMs),
    [onSearchTermChange, debounceMs]
  );

  return (
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="custom"
          variant="outline"
          aria-expanded={open}
          className={cn(
            'hover:text-primary relative h-[30px] w-full justify-between overflow-hidden border-0 px-2 py-1 hover:bg-white',
            triggerClassName,
            disabled && 'cursor-not-allowed opacity-60'
          )}
          disabled={disabled}
          onClick={e => e.stopPropagation()}
          {...TriggerButtonProps}
        >
          {buttonLabel}
          {internalValue.length > 0 && (
            <span
              className="shrink-0 cursor-pointer"
              onClick={e => {
                e.stopPropagation();
                handleClear();
              }}
            >
              <X className="size-4" />
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={cn(
          'w-max max-w-[min(24rem,90vw)] min-w-[var(--radix-popover-trigger-width)] p-0',
          contentClassName
        )}
        align="start"
        {...PopoverContentProps}
      >
        <div className="flex flex-col">
          {(onSearchTermChange || isLocalSearch) && (
            <div className="border-b p-2" onClick={e => e.stopPropagation()}>
              <Input
                fullWidth
                className="border-none"
                startAdornment={
                  <Search className="text-muted-foreground size-5" />
                }
                placeholder={t('search')}
                onChange={e => {
                  e.stopPropagation();
                  if (isLocalSearch) return setLocalSearchTerm(e.target.value);

                  debounceOnSearchTermChange(e.target.value);
                }}
              />
            </div>
          )}
          {!isSingleSelect && (
            <div className="border-b p-2">
              <div
                className="hover:bg-muted/50 flex cursor-pointer items-center space-x-2 rounded-md px-2 py-1.5"
                onClick={e => {
                  e.stopPropagation();
                  toggleAll();
                }}
              >
                <Checkbox
                  checked={areAllSelected}
                  indeterminate={isIndeterminate}
                  onCheckedChange={() => toggleAll()}
                  className="border-muted size-5"
                />
                <p className="text-primary text-sm">{t('selectAll')}</p>
              </div>
            </div>
          )}

          <div className="max-h-[300px] space-y-1 overflow-x-hidden overflow-y-auto p-2">
            {visibleOptions.length === 0 && !isLoading && (
              <div className="text-muted-foreground text-center text-sm">
                {t('noResults')}
              </div>
            )}
            {isLoading && (
              <div className="flex w-full flex-col items-center justify-center gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            )}

            {visibleOptions.map(option => (
              <div
                key={option.value}
                className="hover:bg-muted/50 flex cursor-pointer items-center space-x-2 rounded-md px-2 py-1.5"
                onClick={e => {
                  toggle(option.value);
                  e.stopPropagation();
                }}
              >
                <Checkbox
                  id={option.value}
                  checked={internalValue.includes(option.value)}
                  className="border-muted size-5 shrink-0"
                />
                <span className="text-primary min-w-0 flex-1 cursor-pointer text-sm wrap-break-word whitespace-normal">
                  {option.label}
                </span>
              </div>
            ))}
            {sentryRef && hasNextPage && (
              <div
                ref={sentryRef}
                className="flex w-full items-center justify-center"
              >
                <Skeleton className="h-8 w-full" />
              </div>
            )}
          </div>
          {!isSingleSelect && (
            <div className="flex items-center justify-between border-t p-2">
              <span className="text-muted-foreground text-sm">
                {t('components.selectFilter.selected', {
                  count: selectedCount,
                })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={e => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="hover:text-primary text-sm"
              >
                {t('components.selectFilter.clearAll')}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
